import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { Building2, Calendar, ExternalLink, Fingerprint, Globe, MapPin, TrendingUp } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { BrandMark } from '@/components/ui/BrandMark';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { FinancialsView } from '@/components/portal/FinancialsView';
import { EntityView } from '../EntityView';
import type { LegalEntityInventoryEntityState, LegalEntityRuntimeState } from '#types/types';
type LegalEntityWorkspaceTab = 'FOUNDATION' | 'FINANCIALS' | 'BRANDS';

interface LegalEntityPageProps {
  legalEntityId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const LEGAL_ENTITY_PAGE_TABS = new Set<LegalEntityWorkspaceTab>(['FOUNDATION', 'FINANCIALS', 'BRANDS']);

const normalizeLegalEntityWorkspaceTab = (value: unknown): LegalEntityWorkspaceTab | undefined =>
  typeof value === 'string' && LEGAL_ENTITY_PAGE_TABS.has(value as LegalEntityWorkspaceTab)
    ? (value as LegalEntityWorkspaceTab)
    : undefined;

const translations = {
  en: {
    foundation: 'Foundation',
    financials: 'Financials',
    brands: 'Brands',
    loading: 'Loading...',
    registeredName: 'Registered name',
    noAddress: 'No address provided',
    registrationLabel: 'Registration (SIREN)',
    unavailable: 'Unavailable',
    registered: 'Registered',
    unknown: 'Unknown',
    statusLabel: 'Status',
    lastFiling: 'Last Filing',
    growthLabel: 'Growth',
    linkedBrands: 'Linked brands',
    noLinkedBrands: 'This legal entity is not linked to any brands yet.',
    footprint: 'Footprint',
    mapComingSoon: 'Map coming soon',
    mapPlaceholder: "We're reserving this space for a legal-entity-to-location map as soon as the data model supports it.",
  },
  fr: {
    foundation: 'Fondation',
    financials: 'Finances',
    brands: 'Marques',
    loading: 'Chargement...',
    registeredName: 'Nom enregistre',
    noAddress: 'Aucune adresse',
    registrationLabel: 'Immatriculation (SIREN)',
    unavailable: 'Indisponible',
    registered: 'Enregistre',
    unknown: 'Inconnu',
    statusLabel: 'Statut',
    lastFiling: 'Dernier depot',
    growthLabel: 'Croissance',
    linkedBrands: 'Marques liees',
    noLinkedBrands: "Cette entite legale n'est liee a aucune marque pour le moment.",
    footprint: 'Empreinte',
    mapComingSoon: 'Carte bientot disponible',
    mapPlaceholder: 'Nous reservons cet espace pour une carte entite-legale vers localisations des que le modele de donnees le permet.',
  },
};

function LegalEntityFoundationTab({ legalEntityId }: { legalEntityId: string }) {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const entity = useKernelStore((state) => state.inventory.LegalEntity.registry[legalEntityId]);

  const locationLine = useMemo(
    () => [entity?.city, entity?.postalCode, entity?.country].filter(Boolean).join(' - '),
    [entity?.city, entity?.postalCode, entity?.country]
  );
  const registrationYear = useMemo(
    () => (entity?.registrationDate ? new Date(entity.registrationDate).getFullYear() : null),
    [entity?.registrationDate]
  );

  const handleOpenRegistry = useCallback(() => {
    if (!entity?.registrationNumber) return;
    const url = `https://annuaire-entreprises.data.gouv.fr/rechercher?terme=${encodeURIComponent(entity.registrationNumber)}`;
    window.open(url, '_blank');
  }, [entity?.registrationNumber]);

  if (!entity) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{t.loading}</div>;
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[2.1fr_3fr_2fr]">
        <div className="flex flex-col gap-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
          <div>
            <div className="mb-1 text-[10px] font-medium text-white/30">{t.registeredName}</div>
            <div className="flex items-start gap-2">
              <Building2 size={16} className="mt-0.5 shrink-0 text-white/30" />
              <div>
                <div className="text-lg font-medium leading-snug text-white">{entity.legalName}</div>
                <div className="mt-1 text-xs text-white/50">{locationLine || t.noAddress}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-1 text-[10px] font-medium text-white/30">{t.registrationLabel}</div>
            <button
              type="button"
              onClick={handleOpenRegistry}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-left text-sm font-mono text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <div className="flex items-center gap-2 truncate">
                <Fingerprint size={14} className="shrink-0 text-emerald-400" />
                <span className="truncate">{entity.registrationNumber || t.unavailable}</span>
              </div>
              <ExternalLink size={12} className="shrink-0 text-white/30" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex flex-col gap-1 rounded-xl border border-white/5 bg-white/5 p-3">
              <div className="flex items-center gap-2 text-white/40">
                <Calendar size={12} />
                <span>{t.registered}</span>
              </div>
              <div className="text-base font-medium text-white">{registrationYear ?? t.unknown}</div>
              <div className="text-[10px] text-white/30">{t.statusLabel}: {entity.status || 'Active'}</div>
            </div>
            <div className="flex flex-col gap-1 rounded-xl border border-white/5 bg-white/5 p-3">
              <div className="flex items-center gap-2 text-white/40">
                <TrendingUp size={12} />
                <span>{t.lastFiling}</span>
              </div>
              <div className="text-base font-medium text-white">{entity.lastFinancialYear ?? '-'}</div>
              <div className="text-[10px] text-white/30">
                {t.growthLabel}: {entity.revenueGrowth !== null && entity.revenueGrowth !== undefined ? `${entity.revenueGrowth > 0 ? '+' : ''}${entity.revenueGrowth.toFixed(1)}%` : 'n/a'}
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-[300px]">
          <FinancialsView legalEntity={entity} financials={entity.financials ?? []} />
        </div>

        <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]">
          <div className="flex items-center justify-between border-b border-white/5 bg-white/[0.02] px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-medium text-white/60">
              <MapPin size={14} className="text-white/40" />
              <span>{t.footprint}</span>
            </div>
            <span className="text-[10px] text-white/30">{t.mapComingSoon}</span>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-xs text-white/40">
            <MapPin size={28} className="text-white/20" />
            <p>{t.mapPlaceholder}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegalEntityFinancialsTab({ legalEntityId }: { legalEntityId: string }) {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const entity = useKernelStore((state) => state.inventory.LegalEntity.registry[legalEntityId]);

  if (!entity) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{t.loading}</div>;
  }

  return (
    <div className="p-4">
      <FinancialsView legalEntity={entity} financials={entity.financials ?? []} />
    </div>
  );
}

function LegalEntityBrandsTab({ legalEntityId }: { legalEntityId: string }) {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const entity = useKernelStore((state) => state.inventory.LegalEntity.registry[legalEntityId]);
  const linkedBrands = entity?.linkedBrands ?? [];

  if (!entity) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{t.loading}</div>;
  }

  return (
    <div className="p-4">
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="mb-3 text-[12px] text-white/60">{t.linkedBrands}</div>
        {linkedBrands.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {linkedBrands.map((brand) => (
              <div
                key={brand.id}
                className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
              >
                <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={24} radius={12} />
                <div className="flex flex-col text-left">
                  <span className="truncate text-xs font-medium text-white">{brand.name}</span>
                  <span className="text-[9px] text-white/30">{brand.role}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/10 px-3 py-4 text-center text-xs text-white/30">
            {t.noLinkedBrands}
          </div>
        )}
      </div>
    </div>
  );
}

export const LegalEntityPage = ({ legalEntityId, className, hasEntityRowAbove = false }: LegalEntityPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const entity = useKernelStore(
    (state) => (state.inventory.LegalEntity.registry[legalEntityId] as LegalEntityInventoryEntityState | undefined) ?? null
  );
  const activeTabCandidate = entity?.activeTab ?? entity?.runtime?.activeTab;
  const activeTab = normalizeLegalEntityWorkspaceTab(activeTabCandidate) ?? 'FOUNDATION';

  const patchKernelState = useCallback(
    (patch: Partial<Pick<LegalEntityRuntimeState, 'activeTab'>>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('LegalEntity', legalEntityId, patch);
    },
    [kernel, legalEntityId]
  );

  useEffect(() => {
    if (!kernel.current) return;
    if (normalizeLegalEntityWorkspaceTab(activeTabCandidate)) return;
    kernel.current.patchEntityRuntimeState('LegalEntity', legalEntityId, {
      activeTab: 'FOUNDATION',
    });
  }, [kernel, legalEntityId, activeTabCandidate]);

  useEffect(() => {
    if (!kernel.current || !legalEntityId) return;
    if (!entity || entity.financials === undefined || entity.linkedBrands === undefined) {
      void kernel.current.syncEntity('LegalEntity', legalEntityId);
    }
  }, [kernel, legalEntityId, entity]);

  const tabs = useMemo(
    () => [
      { id: 'FOUNDATION' as const, label: t.foundation },
      { id: 'FINANCIALS' as const, label: t.financials },
      { id: 'BRANDS' as const, label: t.brands },
    ],
    [t.foundation, t.financials, t.brands]
  );

  const handleTabChange = useCallback(
    (nextTab: LegalEntityWorkspaceTab) => {
      patchKernelState({ activeTab: nextTab });
    },
    [patchKernelState]
  );

  return (
    <div className={clsx('flex min-h-[620px] flex-col bg-[#141414] text-white', className)}>
      <EntityPageTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} sticky />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {activeTab === 'FOUNDATION' && <LegalEntityFoundationTab legalEntityId={legalEntityId} />}
        {activeTab === 'FINANCIALS' && <LegalEntityFinancialsTab legalEntityId={legalEntityId} />}
        {activeTab === 'BRANDS' && <LegalEntityBrandsTab legalEntityId={legalEntityId} />}
      </div>
    </div>
  );
};




